import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { useFollowedSuggestion } from '@/hooks/use-followed-suggestion';
import { SuggestedBadge, SpokenValue } from '@/components/custom/suggested-badge';
import { PlaySpokenButton } from '@/components/custom/play-spoken-button';
import { z } from 'zod';
import { toast } from 'sonner';
import { Route } from '@/routes/_authenticated/setup/locations';
import type { ColumnDef } from '@tanstack/react-table';
import type { LocationResponse, ProviderResponse } from '@vagusx/clients';
import { SPOKEN_ADDRESS_MAX_LENGTH, createLocationSchema, sameSpokenLine } from '@vagusx/clients';
// One self-contained file (ADR-322), the same expansion the API applies.
import { spokenAddress, spokenUsAddress } from '@vagusx/agent-core/spoken-address';
import { P, WEEK, hasOpeningHours, readDay } from '@vagusx/shared';
import { User, Clock, Plus, Pencil, X } from 'lucide-react';
import {
  useLocations,
  useLocationProviders,
  useLocation,
  useCreateLocation,
  useUpdateLocation,
} from '@/hooks/use-locations';
import { useAssignProviderLocation, useRemoveProviderLocation } from '@/hooks/use-providers';
import { useHasPermission } from '@/hooks/use-permissions';
import { ProviderPicker } from '@/components/custom/provider-picker';
import { formatPhone } from '@/lib/format';
import { SearchableListSection } from '@/components/custom/searchable-list-section';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { DetailRow, DetailSection } from '@/components/custom/detail-section';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  SheetActions,
  SheetBody,
  SheetCancel,
  SheetLoading,
} from '@/components/custom/sheet-layout';
import { useDiscardGuard } from '@/hooks/use-discard-guard';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// =============================================================================
// Constants
// =============================================================================

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
];

const DAYS = WEEK;
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// =============================================================================
// Form Schema
// =============================================================================

const dayHoursFormSchema = z
  .object({
    enabled: z.boolean(),
    open: z.string(),
    close: z.string(),
  })
  .optional();

/**
 * The form's schema IS the API's (`createLocationSchema` from @vagusx/clients),
 * so a rule or message changes in one place. What differs is only the form's
 * shape: a cleared box is '' rather than absent, the spoken address is the raw
 * box (the API writes it out), and hours are edited per day.
 *
 * The transfer number keeps the API's strict rule (`TRANSFER_TARGET_PATTERN`):
 * it routes a live patient call, and a field that accepted `(555) 123-4567`
 * would look configured and transfer nobody.
 */
const shared = createLocationSchema.shape;
const locationFormSchema = createLocationSchema
  .pick({
    name: true,
    address: true,
    city: true,
    state: true,
    zip: true,
    phone: true,
    fax: true,
    timezone: true,
  })
  .extend({
    transferPhone: shared.transferPhone.or(z.literal('')),
    email: shared.email.or(z.literal('')),
    googleMapsUrl: shared.googleMapsUrl.or(z.literal('')),
    spokenAddress: z
      .string()
      .max(SPOKEN_ADDRESS_MAX_LENGTH, `Keep it under ${SPOKEN_ADDRESS_MAX_LENGTH} characters`),
    isVirtual: z.boolean(),
    monday: dayHoursFormSchema,
    tuesday: dayHoursFormSchema,
    wednesday: dayHoursFormSchema,
    thursday: dayHoursFormSchema,
    friday: dayHoursFormSchema,
    saturday: dayHoursFormSchema,
    sunday: dayHoursFormSchema,
  });

type LocationFormValues = z.infer<typeof locationFormSchema>;

// =============================================================================
// Table Columns
// =============================================================================

function buildColumns(opts: {
  canEdit: boolean;
  onEdit: (id: string) => void;
}): ColumnDef<LocationResponse>[] {
  const cols: ColumnDef<LocationResponse>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      meta: { headerClassName: 'w-[24%]', cellClassName: 'truncate' },
    },
    {
      accessorKey: 'address',
      header: 'Address',
      cell: ({ row }) =>
        `${row.original.address}, ${row.original.city}, ${row.original.state} ${row.original.zip}`,
      meta: { headerClassName: 'w-[36%]', cellClassName: 'truncate' },
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) =>
        row.original.phone ? (
          formatPhone(row.original.phone)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
      meta: { headerClassName: 'w-[18%]' },
    },
    {
      accessorKey: 'isVirtual',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant={row.original.isVirtual ? 'outline' : 'secondary'}>
          {row.original.isVirtual ? 'Virtual' : 'Physical'}
        </Badge>
      ),
      meta: { headerClassName: 'w-[16%]' },
    },
  ];

  if (opts.canEdit) {
    cols.push({
      id: 'actions',
      header: '',
      meta: { headerClassName: 'w-[64px]', cellClassName: 'text-right' },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          // Hidden until the row is hovered; `focus-visible` keeps it reachable
          // by keyboard. Tailwind gates `group-hover` behind `@media (hover:hover)`,
          // so anything with a touchscreen keeps it permanently visible — including
          // a hybrid laptop, whose *primary* input hovers even when the finger the
          // user actually taps with does not. Without that the button would be
          // invisible but still tappable, swallowing the row tap. `any-pointer`
          // (not `hover`) is the test, because it considers every attached input.
          // No `transition-opacity` here: Button's base `transition-all` already
          // covers opacity, and tailwind-merge would drop it for a narrower class.
          className="h-7 w-7 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(any-pointer:coarse)]:opacity-100"
          aria-label={`Edit ${row.original.name}`}
          onClick={(e) => {
            e.stopPropagation();
            opts.onEdit(row.original.id);
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      ),
    });
  }

  return cols;
}

// =============================================================================
// Main Page
// =============================================================================

const providerSearchFields = (p: ProviderResponse): Array<string | null | undefined> => [
  `${p.firstName} ${p.lastName}`,
  p.specialty,
];

export default function LocationsPage(): React.ReactElement {
  const { data, isLoading, error } = useLocations();
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const canEdit = useHasPermission(P.LOCATIONS_EDIT);

  const selectedLocationId = searchParams.id ?? null;
  const isCreating = searchParams.id === 'new';
  const [isEditing, setIsEditing] = useState(false);

  const locations: LocationResponse[] = data?.items ?? [];

  const handleLocationSelect = useCallback(
    (location: LocationResponse) => {
      setIsEditing(false);
      navigate({ to: '/setup/locations', search: { id: location.id }, replace: true });
    },
    [navigate]
  );

  const handleEditFromRow = useCallback(
    (id: string) => {
      setIsEditing(true);
      navigate({ to: '/setup/locations', search: { id }, replace: true });
    },
    [navigate]
  );

  const handleCloseSheet = useCallback(() => {
    setIsEditing(false);
    navigate({ to: '/setup/locations', search: {}, replace: true });
  }, [navigate]);

  const handleCreate = useCallback(() => {
    navigate({ to: '/setup/locations', search: { id: 'new' }, replace: true });
  }, [navigate]);

  const columns = useMemo(
    () => buildColumns({ canEdit, onEdit: handleEditFromRow }),
    [canEdit, handleEditFromRow]
  );

  return (
    <TablePage
      title="Locations"
      description="Manage practice locations and addresses"
      columns={columns}
      data={locations}
      total={locations.length || undefined}
      isLoading={isLoading}
      error={error}
      searchColumn="name"
      searchPlaceholder="Search locations..."
      onRowClick={handleLocationSelect}
      emptyMessage="No locations found."
      errorMessage="Failed to load locations. Please try again."
      tableClassName="table-fixed"
      headerAction={
        canEdit ? (
          <Button size="sm" onClick={handleCreate}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Location
          </Button>
        ) : undefined
      }
    >
      {/* Create Sheet */}
      {isCreating && <LocationEditorSheet onClose={handleCloseSheet} />}

      {/* View/Edit Sheet */}
      {selectedLocationId && !isCreating && (
        <LocationDetailSheet
          locationId={selectedLocationId}
          isEditing={isEditing}
          canEdit={canEdit}
          onEdit={() => setIsEditing(true)}
          onClose={handleCloseSheet}
          onEditDone={() => setIsEditing(false)}
        />
      )}
    </TablePage>
  );
}

// =============================================================================
// Location Detail Sheet (View + Edit toggle)
// =============================================================================

function LocationDetailSheet({
  locationId,
  isEditing,
  canEdit,
  onEdit,
  onClose,
  onEditDone,
}: {
  locationId: string;
  isEditing: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
  onEditDone: () => void;
}): React.ReactElement {
  const { data: location } = useLocation(locationId);
  const { data: locationProviders, isLoading: providersLoading } = useLocationProviders(locationId);

  // Managing a location's providers writes the provider↔location join, so it is
  // gated on PROVIDERS_EDIT (the backend endpoint's permission), not LOCATIONS_EDIT.
  const canEditProviders = useHasPermission(P.PROVIDERS_EDIT);
  const assignLocation = useAssignProviderLocation();
  const removeLocation = useRemoveProviderLocation();
  // Any association mutation in flight disables every association control so a
  // second click can't fire a duplicate request (which would 404/409).
  const isMutating = assignLocation.isPending || removeLocation.isPending;

  const providers = locationProviders ?? [];
  const assignedProviderIds = useMemo(() => providers.map((p) => p.id), [providers]);

  const handleAddProvider = async (providerId: string): Promise<void> => {
    if (!providerId) return;
    try {
      await assignLocation.mutateAsync({ providerId, data: { locationId } });
      toast.success('Provider assigned');
    } catch {
      // global mutation error handler shows the toast
    }
  };

  const handleRemoveProvider = async (providerId: string): Promise<void> => {
    try {
      await removeLocation.mutateAsync({ providerId, locationId });
      toast.success('Provider removed');
    } catch {
      // global mutation error handler shows the toast
    }
  };

  const navigate = useNavigate();

  // View and edit share this one sheet, so entering edit swaps its content
  // instead of sliding a second sheet in, and an edit opened from the table
  // shows a loading state — never the view — until the location arrives.
  const discard = useDiscardGuard();
  const closeSheet = (): void => {
    onEditDone();
    onClose();
  };
  const handleOpenChange = (open: boolean): void => {
    if (open) return;
    if (isEditing) discard.guard(closeSheet);
    else closeSheet();
  };

  return (
    <Sheet open={true} onOpenChange={handleOpenChange}>
      <SheetContent className={cn(LOCATION_SHEET_CLASS, isEditing && 'gap-0')}>
        {isEditing ? (
          location ? (
            <LocationEditor
              location={location}
              onClose={() => discard.guard(closeSheet)}
              onSaved={onEditDone}
              onDirtyChange={discard.setDirty}
            />
          ) : (
            <SheetLoading title="Edit Location" className="px-6" />
          )
        ) : (
          <>
            <SheetHeader className="px-6">
              <SheetTitle>{location?.name}</SheetTitle>
              <SheetDescription>Location details</SheetDescription>
            </SheetHeader>

            {location && (
              <SheetBody className="px-6 pb-6 space-y-2">
                <DetailSection title="Address">
                  <DetailRow label="Street" value={location.address} />
                  <DetailRow label="City" value={location.city} />
                  <DetailRow label="State" value={location.state} />
                  <DetailRow label="ZIP" value={location.zip} />
                  <DetailRow label="Spoken address" value={formatSpokenAddress(location)} />
                </DetailSection>

                <DetailSection title="Contact Information">
                  <DetailRow label="Phone" value={formatPhone(location.phone)} />
                  <DetailRow label="Front desk transfer" value={location.transferPhone} />
                  <DetailRow label="Email" value={location.email} />
                  <DetailRow label="Fax" value={location.fax} />
                </DetailSection>

                <DetailSection title="Settings">
                  <DetailRow label="Type" value={location.isVirtual ? 'Virtual' : 'Physical'} />
                  <DetailRow label="Timezone" value={location.timezone} />
                </DetailSection>

                {location.hours && (
                  <DetailSection title="Hours of Operation">
                    <HoursDisplay hours={location.hours} />
                  </DetailSection>
                )}

                <SearchableListSection
                  key={locationId}
                  title="Providers"
                  items={providers}
                  isLoading={providersLoading}
                  getKey={(provider) => provider.id}
                  searchFields={providerSearchFields}
                  searchPlaceholder="Search by name or specialty..."
                  loadingLabel="Loading providers..."
                  emptyLabel="No providers at this location"
                  noMatchLabel="No providers match your search"
                  action={
                    canEditProviders ? (
                      <ProviderPicker
                        selected=""
                        onChange={handleAddProvider}
                        excludeIds={assignedProviderIds}
                        disabled={isMutating}
                        trigger={
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
                            disabled={isMutating}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                        }
                      />
                    ) : undefined
                  }
                  renderItem={(provider) => (
                    <ProviderCard
                      provider={provider}
                      onClick={() =>
                        navigate({ to: '/setup/providers', search: { id: provider.id } })
                      }
                      canEdit={canEditProviders}
                      disabled={isMutating}
                      onRemove={() => handleRemoveProvider(provider.id)}
                    />
                  )}
                />
              </SheetBody>
            )}

            {canEdit && (
              <SheetActions className="px-6">
                <SheetCancel />
                <Button onClick={onEdit}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit
                </Button>
              </SheetActions>
            )}
          </>
        )}
      </SheetContent>
      {discard.dialog}
    </Sheet>
  );
}

// =============================================================================
// Location Editor (Create / Edit)
// =============================================================================

/** What the voice agent says for these address parts when no one has written it (ADR-322). */
function suggestedSpokenAddress(parts: {
  address: string;
  city: string;
  state: string;
  zip: string;
}): string {
  return spokenUsAddress({
    street: parts.address,
    city: parts.city,
    state: parts.state,
    zip: parts.zip,
  });
}

/** What the agent is told it was built from, for the Suggested badge (ADR-342). */
const SPOKEN_ADDRESS_FROM = 'the address';

/** The address the voice agent says, badged Suggested when it follows the record (ADR-342). */
function formatSpokenAddress(location: LocationResponse): React.ReactNode {
  const suggestion = suggestedSpokenAddress(location);
  const value = location.customSpokenAddress || suggestion;
  // Null, not an empty element, so `DetailRow` hides the row.
  if (!value) return null;
  // Suggested by the same test the API stores null on (see the provider page).
  const suggested =
    !location.customSpokenAddress || sameSpokenLine(location.customSpokenAddress, suggestion);
  return (
    <SpokenValue
      value={value}
      suggested={suggested}
      from={SPOKEN_ADDRESS_FROM}
      label="spoken address"
    />
  );
}

function locationToFormValues(location: LocationResponse | null): LocationFormValues {
  const defaultDay = { enabled: false, open: '08:00', close: '17:00' };

  if (!location) {
    return {
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      transferPhone: '',
      email: '',
      fax: '',
      googleMapsUrl: '',
      spokenAddress: '',
      timezone: 'America/Los_Angeles',
      isVirtual: false,
      monday: { enabled: true, open: '08:00', close: '17:00' },
      tuesday: { enabled: true, open: '08:00', close: '17:00' },
      wednesday: { enabled: true, open: '08:00', close: '17:00' },
      thursday: { enabled: true, open: '08:00', close: '17:00' },
      friday: { enabled: true, open: '08:00', close: '17:00' },
      saturday: defaultDay,
      sunday: defaultDay,
    };
  }

  const hours = location.hours;
  const toDayForm = (day: keyof NonNullable<typeof hours>) => {
    const dh = hours?.[day];
    return dh ? { enabled: true, open: dh.open, close: dh.close } : defaultDay;
  };

  return {
    name: location.name,
    address: location.address,
    city: location.city,
    state: location.state,
    zip: location.zip,
    phone: location.phone ?? '',
    transferPhone: location.transferPhone ?? '',
    email: location.email ?? '',
    fax: location.fax ?? '',
    googleMapsUrl: location.googleMapsUrl ?? '',
    spokenAddress: location.customSpokenAddress ?? suggestedSpokenAddress(location),
    timezone: location.timezone ?? 'America/Los_Angeles',
    isVirtual: location.isVirtual ?? false,
    monday: toDayForm('monday'),
    tuesday: toDayForm('tuesday'),
    wednesday: toDayForm('wednesday'),
    thursday: toDayForm('thursday'),
    friday: toDayForm('friday'),
    saturday: toDayForm('saturday'),
    sunday: toDayForm('sunday'),
  };
}

const LOCATION_SHEET_CLASS = 'w-full max-w-lg sm:max-w-xl';

/** The create sheet. Editing an existing location happens inside its detail sheet. */
function LocationEditorSheet({ onClose }: { onClose: () => void }): React.ReactElement {
  const discard = useDiscardGuard();
  return (
    <Sheet open={true} onOpenChange={(o) => !o && discard.guard(onClose)}>
      <SheetContent className={cn(LOCATION_SHEET_CLASS, 'gap-0')}>
        <LocationEditor
          location={null}
          onClose={() => discard.guard(onClose)}
          onDirtyChange={discard.setDirty}
        />
      </SheetContent>
      {discard.dialog}
    </Sheet>
  );
}

/** Header, form and footer of the location editor — rendered inside a sheet's content. */
function LocationEditor({
  location,
  onClose,
  onSaved,
  onDirtyChange,
}: {
  /** `null` = create a new location. */
  location: LocationResponse | null;
  onClose: () => void;
  onSaved?: () => void;
  /** Lets the hosting sheet guard its X, Escape and Cancel against unsaved changes. */
  onDirtyChange: (isDirty: boolean) => void;
}): React.ReactElement {
  const isEdit = !!location;
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();

  const form = useForm<LocationFormValues>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: locationToFormValues(location),
  });

  const { isDirty } = form.formState;

  useEffect(() => {
    form.reset(locationToFormValues(location));
  }, [location, form]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // The hosting sheet asks before discarding unsaved changes.
  const handleClose = onClose;

  const onSubmit = async (values: LocationFormValues): Promise<void> => {
    const hours: Record<string, { open: string; close: string } | null> = {};
    for (const day of DAYS) {
      const dh = values[day];
      hours[day] = dh?.enabled ? { open: dh.open, close: dh.close } : null;
    }

    if (isEdit) {
      await updateMutation.mutateAsync({
        id: location.id,
        data: {
          name: values.name,
          address: values.address,
          city: values.city,
          state: values.state,
          zip: values.zip,
          phone: values.phone || null,
          transferPhone: values.transferPhone || null,
          email: values.email || null,
          fax: values.fax || null,
          googleMapsUrl: values.googleMapsUrl || null,
          // Sent as shown; the API stores null when it is the suggestion (ADR-342).
          spokenAddress: values.spokenAddress || null,
          timezone: values.timezone,
          isVirtual: values.isVirtual,
          hours,
        },
      });
      toast.success('Location updated');
      onSaved?.();
    } else {
      await createMutation.mutateAsync({
        name: values.name,
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
        phone: values.phone || undefined,
        transferPhone: values.transferPhone || undefined,
        email: values.email || undefined,
        fax: values.fax || undefined,
        googleMapsUrl: values.googleMapsUrl || undefined,
        spokenAddress: values.spokenAddress || undefined,
        timezone: values.timezone,
        isVirtual: values.isVirtual,
        hours,
      });
      toast.success('Location created');
      // Saved, so nothing left to lose: close without asking.
      onDirtyChange(false);
      onClose();
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <SheetHeader className="px-6">
        <SheetTitle>{isEdit ? 'Edit Location' : 'Add Location'}</SheetTitle>
        <SheetDescription>
          {isEdit ? 'Update location information' : 'Add a new practice location'}
        </SheetDescription>
      </SheetHeader>

      <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
        <SheetBody className="px-6 pb-6 space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label>Name</Label>
            <Input {...form.register('name')} placeholder="Location name" />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label>Address</Label>
            <Input {...form.register('address')} placeholder="Street address" />
            {form.formState.errors.address && (
              <p className="text-sm text-destructive">{form.formState.errors.address.message}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>City</Label>
              <Input {...form.register('city')} placeholder="City" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input {...form.register('state')} placeholder="CA" />
            </div>
            <div className="space-y-2">
              <Label>ZIP</Label>
              <Input {...form.register('zip')} placeholder="92307" />
            </div>
          </div>

          <SpokenAddressField form={form} location={location} />

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input {...form.register('phone')} placeholder="(555) 123-4567" />
            </div>
            <div className="space-y-2">
              <Label>Fax</Label>
              <Input {...form.register('fax')} placeholder="(555) 123-4568" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input {...form.register('email')} type="email" placeholder="office@clinic.com" />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          {/* Front-desk transfer target — where a caller who asks for a person
                is bridged to. Left empty, the agent says so and takes a message. */}
          <div className="space-y-2">
            <Label>Front desk transfer number</Label>
            <Input {...form.register('transferPhone')} placeholder="+15551234567" />
            <p className="text-sm text-muted-foreground">
              Where callers are transferred when they ask to speak with a person. Leave empty and
              the agent will explain it can&apos;t transfer, then take a message.
            </p>
            {form.formState.errors.transferPhone && (
              <p className="text-sm text-destructive">
                {form.formState.errors.transferPhone.message}
              </p>
            )}
          </div>

          {/* Google Maps link — included in confirmation SMS when set */}
          <div className="space-y-2">
            <Label>Google Maps URL</Label>
            <Input
              {...form.register('googleMapsUrl')}
              type="url"
              placeholder="https://maps.app.goo.gl/..."
            />
            <p className="text-xs text-muted-foreground">
              Sent as a directions link in appointment confirmation texts.
            </p>
            {form.formState.errors.googleMapsUrl && (
              <p className="text-sm text-destructive">
                {form.formState.errors.googleMapsUrl.message}
              </p>
            )}
          </div>

          {/* Settings */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select
                value={form.watch('timezone')}
                onValueChange={(v) => form.setValue('timezone', v, { shouldDirty: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {US_TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  checked={form.watch('isVirtual')}
                  onCheckedChange={(v) =>
                    form.setValue('isVirtual', v === true, { shouldDirty: true })
                  }
                />
                <span className="text-sm">Virtual location</span>
              </div>
            </div>
          </div>

          {/* Operating Hours */}
          <div className="space-y-3">
            <Label>Hours of Operation</Label>
            {DAYS.map((day, i) => {
              const enabled = form.watch(`${day}.enabled`);
              return (
                <div key={day} className="flex items-center gap-3">
                  <Checkbox
                    checked={enabled}
                    onCheckedChange={(v) =>
                      form.setValue(`${day}.enabled`, v === true, { shouldDirty: true })
                    }
                  />
                  <span className="text-sm w-24">{DAY_LABELS[i]}</span>
                  {enabled ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        className="w-28 h-8 text-sm"
                        {...form.register(`${day}.open`)}
                      />
                      <span className="text-muted-foreground text-sm">to</span>
                      <Input
                        type="time"
                        className="w-28 h-8 text-sm"
                        {...form.register(`${day}.close`)}
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">Closed</span>
                  )}
                </div>
              );
            })}
          </div>
        </SheetBody>

        <SheetActions className="px-6">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <SaveButton type="submit" isPending={isPending} disabled={!isDirty} />
        </SheetActions>
      </form>
    </>
  );
}

// =============================================================================
// Shared Components
// =============================================================================

/**
 * Compact, divider-separated row for a provider at this location. The Remove
 * action renders only when `canEdit` and stops propagation so it doesn't trigger
 * the row's navigate-to-provider click.
 */
function ProviderCard({
  provider,
  onClick,
  canEdit = false,
  disabled = false,
  onRemove,
}: {
  provider: ProviderResponse;
  onClick: () => void;
  canEdit?: boolean;
  disabled?: boolean;
  onRemove?: () => void;
}): React.ReactElement {
  return (
    <div
      className="group flex cursor-pointer items-center gap-2.5 border-t py-2.5 first:border-t-0 hover:bg-accent/40"
      onClick={onClick}
    >
      <User className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          {provider.firstName} {provider.lastName}
        </div>
        {provider.specialty && (
          <div className="truncate text-xs text-muted-foreground">{provider.specialty}</div>
        )}
      </div>
      {canEdit && onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
          aria-label={`Remove ${provider.firstName} ${provider.lastName}`}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function HoursDisplay({
  hours,
}: {
  hours: NonNullable<LocationResponse['hours']>;
}): React.ReactElement | null {
  // The shared reading rules (ADR-359), so this page and the voice agent agree
  // on what a day means: missing or empty is closed.
  if (!hasOpeningHours(hours)) return null;

  return (
    <div className="flex items-start gap-2">
      <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
      <div className="text-sm space-y-1">
        {DAYS.map((day, i) => {
          const dayHours = hours[day];
          // An unreadable day still shows what is stored, so staff can see it
          // and fix it; only a missing or empty day reads "Closed".
          const closed = readDay(hours, day).kind === 'closed';
          return (
            <div key={day} className="flex gap-2">
              <span className="text-muted-foreground w-24">{DAY_LABELS[i]}</span>
              <span>{closed || !dayHours ? 'Closed' : `${dayHours.open} - ${dayHours.close}`}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The spoken address (ADR-342): what the voice agent says when a caller asks
 * where the office is, exactly as written. It follows the address — written
 * out in full words — until someone types in it, and goes back to following it
 * when emptied. Abbreviations are written out when the box loses focus.
 */
function SpokenAddressField({
  form,
  location,
}: {
  form: ReturnType<typeof useForm<LocationFormValues>>;
  location: LocationResponse | null;
}): React.ReactElement {
  const [address, city, state, zip] = useWatch({
    control: form.control,
    name: ['address', 'city', 'state', 'zip'],
  });
  // A new location, or one whose line follows the address, keeps following it.
  const { field, value, diverged, auto, follow } = useFollowedSuggestion({
    form,
    name: 'spokenAddress',
    suggestion: suggestedSpokenAddress({ address, city, state, zip }),
    expand: spokenAddress,
    initiallyFollows: !location?.customSpokenAddress,
  });
  const error = form.formState.errors.spokenAddress?.message;

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        Spoken address
        {auto && <SuggestedBadge from={SPOKEN_ADDRESS_FROM} />}
        {diverged && (
          <Button
            type="button"
            variant="link"
            className="ml-auto h-auto p-0 text-xs font-normal"
            onClick={follow}
          >
            Use suggested
          </Button>
        )}
      </Label>
      <div className="flex items-center gap-2">
        <Input {...field} placeholder="18095 Highway 18, Apple Valley, California, 9 2 3 0 7" />
        <PlaySpokenButton text={value} label="spoken address" />
      </div>
      <p className="text-xs text-muted-foreground">
        Exactly what the voice agent says when a caller asks for the address, in full words.
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
